import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const euro = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(n || 0));
const MESI = ['', 'gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
const METODI = { BONIFICO: 'bonifico', BANCOMAT: 'bancomat', POS: 'carta/POS', ASSEGNO: 'assegno', CONTANTI: 'contanti', ALTRO: 'altro metodo' };
const STATO = {
  IN_VERIFICA: { label: 'Da verificare', cls: 'bg-yellow-100 text-yellow-800' },
  VERIFICATA: { label: 'Verificata e registrata', cls: 'bg-green-100 text-green-800' },
  RIFIUTATA: { label: 'Rifiutata', cls: 'bg-red-100 text-red-800' },
};
const dataIt = (d) => (d || '').slice(0, 10).split('-').reverse().join('/');

// Verifica dei pagamenti dichiarati dai condomini. «Verifica» registra davvero gli incassi sulle quote: si scalano dalla scheda del condomino
// e da quella dell'amministratore, come un incasso registrato a mano. Nessuna email o PEC parte da qui.
export const DichiarazioniPagamento = () => {
  const { token, selectedCondominium } = useAuth();
  const condoId = selectedCondominium?.condominium?.id;
  const puoVerificare = selectedCondominium?.role === 'ADMIN';
  const base = `${API_URL}/api/condominiums/${condoId}/dichiarazioni-pagamento`;
  const intest = useMemo(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);
  const [righe, setRighe] = useState([]);
  const [filtro, setFiltro] = useState('IN_VERIFICA');
  const [importi, setImporti] = useState({});
  const [date, setDate] = useState({});
  const [lavoro, setLavoro] = useState(false);

  const carica = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(base, { headers: intest });
      if (!res.ok) throw new Error('Non riesco a caricare le dichiarazioni');
      setRighe((await res.json()).dichiarazioni || []);
    } catch (e) { toast.error(e.message); }
  }, [base, condoId, intest]);
  useEffect(() => { carica(); }, [carica]);

  const scarica = async (d, a) => {
    try {
      const res = await fetch(`${base}/${d.id}/allegati/${a.id}`, { headers: intest });
      if (!res.ok) throw new Error('Allegato non disponibile');
      window.open(URL.createObjectURL(await res.blob()), '_blank');
    } catch (e) { toast.error(e.message); }
  };

  const esito = async (d, valore) => {
    let corpo;
    if (valore === 'VERIFICATA') {
      const importo = Number(String(importi[d.id] ?? d.totale).replace(',', '.'));
      if (!window.confirm(`Confermi che sul conto risultano accreditati ${euro(importo)}? Gli incassi verranno registrati sulle ${d.voci.length} quote.`)) return;
      corpo = { esito: 'VERIFICATA', importo_accreditato: importo, data_incasso: date[d.id] || d.data_bonifico || null };
    } else {
      const motivo = window.prompt('Motivo del rifiuto (lo vede il condomino):');
      if (!motivo) return;
      corpo = { esito: 'RIFIUTATA', motivo };
    }
    setLavoro(true);
    try {
      const res = await fetch(`${base}/${d.id}/esito`, { method: 'POST', headers: intest, body: JSON.stringify(corpo) });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof j.detail === 'string' ? j.detail : 'Operazione non riuscita');
      toast.success(valore === 'VERIFICATA' ? `Verificata: ${j.incassi_registrati} incassi registrati` : 'Dichiarazione rifiutata');
      await carica();
    } catch (e) { toast.error(e.message); }
    setLavoro(false);
  };

  const visibili = righe.filter((r) => !filtro || r.stato === filtro);

  return (
    <div className="space-y-6">
      <PageHeader title="Pagamenti dichiarati dai condomini" description="Verifica l'accredito sul conto: confermando, gli incassi si registrano da soli sulle quote." />
      <div className="flex flex-wrap gap-2">
        {['IN_VERIFICA', 'VERIFICATA', 'RIFIUTATA', ''].map((k) => (
          <Button key={k || 'tutte'} size="sm" variant={filtro === k ? 'default' : 'outline'} onClick={() => setFiltro(k)}>
            {k ? STATO[k].label : 'Tutte'}{k ? ` (${righe.filter((r) => r.stato === k).length})` : ''}
          </Button>
        ))}
      </div>
      {visibili.length === 0 && <p className="text-sm text-muted-foreground">Nessuna dichiarazione.</p>}
      {visibili.map((d) => (
        <Card key={d.id}>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">{d.autore} · {d.unita}</CardTitle>
              <Badge className={STATO[d.stato]?.cls}>{STATO[d.stato]?.label || d.stato}</Badge>
            </div>
            <CardDescription>
              Dichiarato il {dataIt(d.creato_il)} · {euro(d.totale)} · {METODI[d.metodo] || d.metodo || 'bonifico'}{d.data_bonifico ? ` del ${dataIt(d.data_bonifico)}` : ''}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <ul className="list-disc ml-5">
              {d.voci.map((v) => <li key={v.quote_id}>{MESI[v.mese]} {v.anno} · {v.unita}: {euro(v.residuo)}</li>)}
            </ul>
            {d.note && <p className="text-muted-foreground">Note: {d.note}</p>}
            {d.stato === 'RIFIUTATA' && d.motivo_rifiuto && <p className="text-red-700">Motivo: {d.motivo_rifiuto}</p>}
            <div className="flex flex-wrap gap-2">
              {(d.allegati || []).map((a) => <Button key={a.id} type="button" size="sm" variant="outline" onClick={() => scarica(d, a)}>{a.nome}</Button>)}
            </div>
            {puoVerificare && d.stato === 'IN_VERIFICA' && (
              <div className="flex flex-wrap items-end gap-3 rounded-md border p-3">
                <div><Label>Importo accreditato (€)</Label>
                  <Input className="w-36" value={importi[d.id] ?? String(d.totale).replace('.', ',')} onChange={(e) => setImporti({ ...importi, [d.id]: e.target.value })} /></div>
                <div><Label>Data accredito</Label>
                  <Input type="date" className="w-44" max={new Date().toISOString().slice(0, 10)} value={date[d.id] ?? d.data_bonifico ?? ''} onChange={(e) => setDate({ ...date, [d.id]: e.target.value })} /></div>
                <Button disabled={lavoro} onClick={() => esito(d, 'VERIFICATA')}>Verifica e registra gli incassi</Button>
                <Button disabled={lavoro} variant="outline" onClick={() => esito(d, 'RIFIUTATA')}>Rifiuta</Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default DichiarazioniPagamento;
